import { stringify } from "querystring";
import { forwardRef, Ref, useContext, useState } from "react";

import { Button, useWindowSize } from "@newsela/angelou";
import { useRouter } from "next/router";
import AIToggle from "@/components/AIComponents/AIToggle";
import { RefContext } from "@/base/NewselaGlobalLayoutV2/RefContext.provider";
import { FilterHeader } from "@/search/Langston/FilterPanel/FilterHeader";
import {
  backToTopButtonCss,
  getFilterContainerPositionCss,
} from "@/search/Langston/SearchPage/SearchPage.styles";
import { StandardsSearchProvider } from "@/search/Langston/StandardsSearchProvider";
import { useLunaToggle } from "@/search/LunaToggleProvider";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { Filters } from "./filters";
import {
  getFilterPanelContainerCss,
  getFilterPanelFiltersSectionVisibilityCss,
  toggleWrapperCss,
} from "./FilterPanel.styles";
import { FilterControls } from "./FilterControls";
import { LunaFilterPanel } from "./LunaFilterPanel";

export const FilterPanel = forwardRef((_, ref: Ref<HTMLDivElement>) => {
  const { push, query, pathname } = useRouter();
  const { height: windowHeight } = useWindowSize();
  const { layoutRef } = useContext(RefContext);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const onClickBackToTop = () => {
    if (layoutRef?.current) {
      layoutRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const {
    isAISearchEnabled,
    shouldShowAISearch,
    canSeeAiSmartSearch,
    handleAISearchToggle,
  } = useLunaToggle();

  const handleSearchWithLunaToggle = () => {
    const newValue = !isAISearchEnabled;
    if (pathname === "/search") {
      const searchParams = new URLSearchParams(stringify(query));
      searchParams.delete(LangstonParameterSchema.prompt.key);
      void push({
        pathname: "/search",
        search: `?${searchParams.toString()}`,
      });
    }
    handleAISearchToggle(newValue);
  };

  const renderDefaultFilterPanel = () => {
    return (
      <div css={getFilterContainerPositionCss}>
        <div css={getFilterPanelContainerCss(isFilterPanelOpen)} ref={ref}>
          {canSeeAiSmartSearch ? (
            <div css={toggleWrapperCss}>
              <AIToggle
                pressed={isAISearchEnabled}
                onToggle={handleSearchWithLunaToggle}
                ariaLabel="Search with Luna"
                labelText="Search with Luna"
                qaSelector="search-with-luna-toggle"
                description="Toggle Search with Luna"
              />
            </div>
          ) : null}

          <FilterHeader
            isFilterPanelOpen={isFilterPanelOpen}
            setIsFilterPanelOpen={setIsFilterPanelOpen}
          />

          <div
            css={getFilterPanelFiltersSectionVisibilityCss(
              isFilterPanelOpen,
              windowHeight,
            )}
          >
            <FilterControls />
            <Filters />
          </div>
        </div>

        <Button
          __cssFor={backToTopButtonCss}
          onClick={onClickBackToTop}
          flavor={Button.flavors.primary}
          dataProps={{ "data-qa-selector": "search-scroll-to-top" }}
        >
          Scroll back to top
        </Button>
      </div>
    );
  };

  const renderSearchWithLunaFilterPanel = () => {
    return (
      <LunaFilterPanel
        isAISearchEnabled={isAISearchEnabled}
        onToggle={handleAISearchToggle}
      />
    );
  };
  return (
    <StandardsSearchProvider>
      {canSeeAiSmartSearch && shouldShowAISearch
        ? renderSearchWithLunaFilterPanel()
        : renderDefaultFilterPanel()}
    </StandardsSearchProvider>
  );
});
